import { useState, lazy, Suspense } from "react";
import { Menu, X } from "lucide-react";
import logo from "@/assets/logo.svg";
import pikeGradient from "@/assets/pike-gradient.webp";
import { useLanguage } from "@/contexts/LanguageContext";
import DesktopNav from "./header/DesktopNav";
import MobileMenu from "./header/MobileMenu";
import SocialIcons from "./header/SocialIcons";
import LanguageSelector from "./header/LanguageSelector";
const SiteSearch = lazy(() => import("./header/SiteSearch"));

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useLanguage();

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Dotted gradient overlay — full opacity, behind content but above white bg */}
      <img
        src={pikeGradient}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 w-full h-full object-cover object-right-top z-[1] opacity-40 -scale-x-100 -scale-y-100"
      />
      {/* Top row: white bar — logo left, utilities right */}
      <div className="hidden lg:block relative">
        <div className="absolute inset-0 bg-background z-0" />
        <div className="container flex items-center justify-between h-[72px] relative z-[2]">
          <a href="/" className="flex items-center shrink-0">
            <img src={logo} alt="Kočevar" className="h-14" width="306" height="56" />
          </a>
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-4">
              <LanguageSelector />
              <div className="w-px h-4 bg-border/50" />
              <SocialIcons />
            </div>
            <a
              href="/#contact"
              className="relative inline-flex items-center justify-center px-7 py-2.5 text-sm font-semibold bg-primary text-primary-foreground rounded-lg hover:bg-primary/85 transition-colors z-[3]"
            >
              <span className="absolute inset-0 bg-primary rounded-lg z-0" />
              <span className="relative z-[1]">{t.sendInquiry}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom row: red navigation bar — desktop */}
      <div className="hidden lg:block relative">
        <div className="absolute inset-0 bg-primary z-0" />
        <div className="container flex items-center justify-center h-[44px] relative z-[2]">
          <DesktopNav />
        </div>
      </div>

      {/* Mobile: single row with logo + hamburger */}
      <div className="flex lg:hidden items-center justify-between h-[56px] px-4 relative">
        <div className="absolute inset-0 bg-background z-0" />
        <a href="/" className="flex items-center shrink-0 relative z-[2]">
          <img src={logo} alt="Kočevar" className="h-11" width="240" height="44" />
        </a>
        <div className="flex items-center gap-1 relative z-[2]">
          <Suspense fallback={null}><SiteSearch /></Suspense>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2.5 text-foreground"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && <MobileMenu onClose={() => setMobileOpen(false)} />}
    </header>
  );
};

export default Header;
